import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { REGIONS, ASKS } from "../lib/content";
import { renderCard, downloadCard } from "../lib/poemCard";

export default function PledgeWall() {
  const [data, setData] = useState({ total: 0, by_region: {}, recent: [] });
  const [form, setForm] = useState({ name: "", region: "Canterbury", comment: "" });
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState(false);
  const [cardBusy, setCardBusy] = useState("");

  const load = () => api.get("/pledges").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pledges", form);
      setSigned(true);
      setForm({ name: "", region: "Canterbury", comment: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const shareCard = async (fmt) => {
    setCardBusy(fmt);
    try {
      const canvas = await renderCard(fmt);
      downloadCard(canvas, fmt);
      toast.success("Card downloaded — post it with #NoToMeth and tag @movementbreakthecycle");
    } catch {
      toast.error("Couldn't build the card — please try again.");
    } finally {
      setCardBusy("");
    }
  };

  return (
    <section id="pledge" className="bg-[#1E4B3E] text-[#F5F2EC] py-24 sm:py-32 relative overflow-hidden grain">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mono-label text-[#F59E0B] mb-4">The campaign commitment</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-6">
              I choose to<br />break the cycle
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="opacity-90 italic font-grotesk text-lg mb-8 max-w-md">
              "I will help prevent methamphetamine harm, support people seeking recovery, and help strengthen my community."
            </motion.p>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-10">
              <span data-testid="pledge-live-counter" className="font-display font-extrabold text-7xl sm:text-8xl text-[#F59E0B] tracking-tight">
                {data.total.toLocaleString()}
              </span>
              <span className="block mono-label opacity-80 mt-2">people have signed the pledge</span>
            </motion.div>

            {signed ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-black/20 rounded-2xl p-6 max-w-md" data-testid="pledge-success-panel">
                <div className="flex items-center gap-2.5 mb-3">
                  <CheckCircle2 size={20} className="text-[#F59E0B]" />
                  <p className="font-display font-bold">Kia kaha — your name is on the wall.</p>
                </div>
                <p className="text-xs opacity-80 leading-relaxed mb-4">
                  Carry it further: download the final verse of Shattered Glass as a branded card and share it to your feed or story. Every share reaches someone we can't.
                </p>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => shareCard("post")} disabled={!!cardBusy} data-testid="pledge-share-post"
                    className="flex-1 flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-50 text-black font-bold rounded-lg py-2.5 text-xs transition-colors">
                    {cardBusy === "post" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Post · 1:1
                  </button>
                  <button onClick={() => shareCard("story")} disabled={!!cardBusy} data-testid="pledge-share-story"
                    className="flex-1 flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 disabled:opacity-50 font-bold rounded-lg py-2.5 text-xs transition-colors">
                    {cardBusy === "story" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Story · 9:16
                  </button>
                </div>
                <button onClick={() => setSigned(false)} className="text-[11px] opacity-60 hover:opacity-100 underline" data-testid="pledge-sign-another">Sign another name</button>
              </motion.div>
            ) : (
              <form data-testid="pledge-form" onSubmit={submit} className="bg-black/20 rounded-2xl p-6 space-y-3 max-w-md">
                <div className="grid grid-cols-2 gap-3">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
                    data-testid="pledge-input-name" maxLength={80}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm placeholder:text-white/50 focus:outline-none focus:border-[#F59E0B]" />
                  <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} data-testid="pledge-select-region"
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#F59E0B] [&>option]:text-black">
                    {REGIONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={2} maxLength={500}
                  placeholder="Why this matters to you (optional)" data-testid="pledge-input-comment"
                  className="w-full bg-white/10 border border-[#212636]/0 border-white/20 rounded-lg px-3 py-2.5 text-sm placeholder:text-white/50 focus:outline-none focus:border-[#F59E0B]" />
                <button type="submit" disabled={busy} data-testid="pledge-submit-button"
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] disabled:opacity-50 text-black font-bold rounded-lg py-3 text-sm transition-colors">
                  {busy ? "Signing…" : "Add my name"}
                </button>
              </form>
            )}
          </div>

          <div>
            <p className="mono-label opacity-70 mb-4">The wall · live</p>
            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2" data-testid="pledge-wall-feed">
              {data.recent.map((p) => (
                <motion.div key={p.id} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} className="bg-white/10 rounded-xl px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm">{p.name}</span>
                    <span className="mono-label text-[#F59E0B] text-[9px]">{p.region}</span>
                  </div>
                  {p.comment && <p className="text-xs opacity-80 mt-1 leading-relaxed">{p.comment}</p>}
                </motion.div>
              ))}
              {data.recent.length === 0 && <p className="text-sm opacity-60">Be the first name on the wall.</p>}
            </div>

            <div className="mt-8 bg-black/20 rounded-2xl p-6">
              <p className="mono-label text-[#F59E0B] mb-3">The petition asks</p>
              <ul className="text-xs opacity-90 space-y-1.5 leading-relaxed columns-1 sm:columns-2 gap-6">
                {ASKS.map((a) => <li key={a} className="break-inside-avoid">— {a}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
